import { useMemo } from 'react';
import { Box, Card, Typography, Stack, CircularProgress } from '@mui/material';
import noticeIcon from '../../assets/WidgetsIcon/notice.svg';
import curveArrowRed from '../../assets/WidgetsIcon/curve-arrow.svg';
import graphIcon from '../../assets/WidgetsIcon/graph-icon.svg';
import curveArrowGreen from '../../assets/WidgetsIcon/curve-arrow-green-down.svg';
import FilterOverview from '../../components/Incidents/FilterOverview';
import { callStatusColorMatching } from '../Incidents/utils/statusMatching';

function calculateStatusPercentage(status) {
  if (status) {
    const total = status.open + status.closed;
    if (total === 0) return { open: 0, closed: 0 };

    return {
      open: ((status.open / total) * 100).toFixed(0) + '%',
      closed: ((status.closed / total) * 100).toFixed(0) + '%',
    };
  }
}

function calculatePercentageNeris(status) {
  if (status) {
    const total = Object.values(status).reduce((sum, value) => sum + value, 0);
    if (total === 0) return {};

    return Object.fromEntries(
      Object.entries(status).map(([key, value]) => [
        key || 'NOT_SET',
        ((value / total) * 100).toFixed(0) + '%',
      ])
    );
  }
}

const TotalIncidents = ({ dateRange, data, loading }) => {
  const statusPercentage = useMemo(
    () => calculateStatusPercentage(data.status),
    [data]
  );
  const nerisStatusPercentage = useMemo(
    () => calculatePercentageNeris(data.neris_status),
    [data]
  );

  return (
    <Card
      className="p-3"
      sx={{
        boxShadow: '0px 4px 4px 0px #0000000F',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            marginLeft: 1,
            fontWeight: 700,
            lineHeight: '20px',
          }}
        >
          General information
        </Typography>
        <FilterOverview />
      </Stack>
      {loading ? (
        <Box
          sx={{ minHeight: '100px' }}
          className="w-100 h-100 d-flex justify-content-center align-items-center"
        >
          <CircularProgress />
        </Box>
      ) : !loading && Object.keys(data).length === 0 ? (
        <Typography className="mt-2" color="#6E7C87">
          No data search.
        </Typography>
      ) : (
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box
            className="d-flex flex-column justify-content-between"
            sx={{
              px: 2,
              py: '12px',
              border: '1px solid #E1E1E1',
              borderRadius: '8px',
              height: 170,
              minWidth: 317,
              flex: 9,
            }}
          >
            <Box className="d-flex">
              <img src={noticeIcon} alt="noticeIcon" />{' '}
              <Typography
                variant="h6"
                sx={{
                  marginLeft: 1,
                  fontWeight: 700,
                  lineHeight: '20px',
                }}
              >
                Total Incidents
              </Typography>
            </Box>
            <Box className="d-flex align-items-baseline mt-3">
              <Typography
                variant="h4"
                sx={{
                  marginRight: 1,
                  fontWeight: 700,
                }}
              >
                {data.count.now}
              </Typography>
              <img
                src={data.count.percent <= 0 ? curveArrowGreen : curveArrowRed}
                alt="curveArrow"
                className="me-1"
              />
              <Typography
                variant="body2"
                sx={{
                  color: data.count.percent <= 0 ? '#45856B' : '#C71818',
                  fontWeight: 600,
                }}
                className="me-1"
              >
                {data.count.percent}%
              </Typography>
              <Typography variant="body2" sx={{ color: '#00000078' }}>
                previous {dateRange} days
              </Typography>
            </Box>

            {/* <Box className="d-flex mt-3">
            <Box className="d-flex me-4">
              <Typography variant="body2">
                <Typography
                  component="span"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                  }}
                >
                  190{" "}
                </Typography>
                Fire incidents
              </Typography>
            </Box>
            <Box className="d-flex">
              <Typography variant="body2">
                <Typography
                  component="span"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                  }}
                >
                  373{" "}
                </Typography>
                EMS incidents
              </Typography>
            </Box>
          </Box> */}
          </Box>
          <Box
            className="d-flex flex-column justify-content-between"
            sx={{
              px: 2,
              py: '12px',
              border: '1px solid #E1E1E1',
              borderRadius: '8px',
              height: 170,
              flex: 12,
            }}
          >
            <Box className="d-flex">
              <img src={graphIcon} alt="graphIcon" />{' '}
              <Typography
                variant="h6"
                sx={{
                  marginLeft: 1,
                  fontWeight: 700,
                  lineHeight: '20px',
                }}
              >
                Open vs. Closed Incidents
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: '4px',
                  background: callStatusColorMatching('open')?.bg,
                  minWidth: '130px',
                  height: '105px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Open
                </Typography>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {data.status.open}
                </Typography>
                <Typography variant="body2">
                  {statusPercentage.open} incidents
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '4px',
                  background: callStatusColorMatching('closed')?.bg,
                  minWidth: '130px',
                  height: '105px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Closed
                </Typography>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {data.status.closed}
                </Typography>
                <Typography variant="body2">
                  {statusPercentage.closed} incidents
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            className="d-flex flex-column justify-content-between"
            sx={{
              px: 2,
              py: '12px',
              border: '1px solid #E1E1E1',
              borderRadius: '8px',
              minHeight: 170,
              flex: 30,
              height: 'stretch',
            }}
          >
            <Box
              className="d-flex"
              sx={{
                mb: '20px',
              }}
            >
              <img src={graphIcon} alt="graphIcon" />{' '}
              <Typography
                variant="h6"
                sx={{
                  marginLeft: 1,
                  fontWeight: 700,
                  lineHeight: '20px',
                }}
              >
                NERIS Status
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: '4px',
                  background: '#D0EDFD',
                  minWidth: '130px',
                  minHeight: '105px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Sending
                </Typography>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {data.neris_status.SUBMITTED || '0'}
                </Typography>
                <Typography variant="body2">
                  {nerisStatusPercentage.SUBMITTED || '0%'} incidents
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '4px',
                  background: '#FFE6F7',
                  minWidth: '130px',
                  height: '105px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Incomplete
                </Typography>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {data.neris_status.REJECTED || '0'}
                </Typography>
                <Typography variant="body2">
                  {nerisStatusPercentage.REJECTED || '0%'} incidents
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: '4px',
                  background: '#DCFCE7',
                  minWidth: '130px',
                  height: '105px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Complete
                </Typography>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {data.neris_status.APPROVED || '0'}
                </Typography>
                <Typography variant="body2">
                  {nerisStatusPercentage.APPROVED || '0%'} incidents
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: '4px',
                  background: '#FEF3C7',
                  minWidth: '130px',
                  height: '105px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Waiting
                </Typography>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {data.neris_status.PENDING_APPROVAL || '0'}
                </Typography>
                <Typography variant="body2">
                  {nerisStatusPercentage.PENDING_APPROVAL || '0%'} incidents
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: '4px',
                  background: '#FEE2E2',
                  minWidth: '130px',
                  height: '105px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Error
                </Typography>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {data.neris_status.FAILED || '0'}
                </Typography>
                <Typography variant="body2">
                  {nerisStatusPercentage.FAILED || '0%'} incidents
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      )}
    </Card>
  );
};

export default TotalIncidents;
